import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Settings, ChevronLeft, ChevronRight, PlusCircle, MinusCircle, TrendingDown, RefreshCw, ArrowRightLeft, X } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Transaction } from '@/types/asset';

interface RecentActivityCardProps {
  transactions: Transaction[];
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

type TimeRange = 'week' | '14days' | 'month';

const TIME_RANGE_CONFIG = {
  week: { label: 'Last Week', days: 7 },
  '14days': { label: 'Last 14 Days', days: 14 },
  month: { label: 'Last Month', days: 30 },
};

// Transaction type colors and icons
const getTransactionStyle = (type: string) => {
  const styles: Record<string, { bgColor: string; borderColor: string; icon: React.ComponentType<{ className?: string }> }> = {
    addition: {
      bgColor: 'bg-green-50 dark:bg-green-950/30',
      borderColor: 'border-green-200 dark:border-green-800',
      icon: PlusCircle
    },
    acquisition: { 
      bgColor: 'bg-green-50 dark:bg-green-950/30', 
      borderColor: 'border-green-200 dark:border-green-800',
      icon: PlusCircle 
    },
    disposal: {
      bgColor: 'bg-red-50 dark:bg-red-950/30',
      borderColor: 'border-red-200 dark:border-red-800',
      icon: MinusCircle
    },
    depreciation: {
      bgColor: 'bg-blue-50 dark:bg-blue-950/30',
      borderColor: 'border-blue-200 dark:border-blue-800',
      icon: TrendingDown
    },
    revaluation: {
      bgColor: 'bg-purple-50 dark:bg-purple-950/30',
      borderColor: 'border-purple-200 dark:border-purple-800',
      icon: RefreshCw
    },
    transfer: {
      bgColor: 'bg-orange-50 dark:bg-orange-950/30',
      borderColor: 'border-orange-200 dark:border-orange-800',
      icon: ArrowRightLeft
    },
  };
  return styles[type.toLowerCase()] || {
    bgColor: 'bg-gray-50 dark:bg-gray-950/30',
    borderColor: 'border-gray-200 dark:border-gray-800',
    icon: PlusCircle
  };
};

export function RecentActivityCard({ transactions, isCollapsed = false, onToggleCollapse }: RecentActivityCardProps): React.ReactElement {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<TimeRange>('week');

  console.log('RecentActivityCard - Received transactions:', transactions?.length || 0);

  // Helper function to convert status to camel case
  const formatStatusText = (status: string): string => {
    return status
      .split(/[\s_-]+/) // Split by space, underscore, or hyphen
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Filter transactions based on selected time range
  const getFilteredTransactions = () => {
    const days = TIME_RANGE_CONFIG[timeRange].days;
    const cutoffDate = subDays(new Date(), days);

    console.log('Filtering transactions:', {
      totalTransactions: transactions?.length || 0,
      timeRange,
      days,
      cutoffDate: cutoffDate.toISOString()
    });

    const filtered = transactions
      .filter(transaction => {
        const transDate = new Date(transaction.transactionDate);
        const isAfterCutoff = transDate >= cutoffDate;
        return isAfterCutoff;
      })
      .sort((a: any, b: any) =>
        new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime()
      );

    return filtered;
  };

  const recentActivity = getFilteredTransactions();

  // Collapsed view - just a vertical bar with toggle button
  if (isCollapsed) {
    return (
      <div className="relative h-full bg-white dark:bg-black border-l-2 border-border flex flex-col items-center py-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="h-8 w-8 text-[oklch(0.5_0.09_194.77)] hover:text-[oklch(0.5_0.09_194.77)]/80"
          title="Expand Recent Activity"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="mt-4 writing-mode-vertical text-sm font-semibold text-[oklch(0.5_0.09_194.77)]">
          <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
            Recent Activity
          </span>
        </div>
      </div>
    );
  }

  // Expanded view - full panel with vertical timeline
  return (
    <div className="relative h-full bg-white dark:bg-black border-l-2 border-border flex flex-col">
      {/* Header with collapse button */}
      <div className="flex-shrink-0 border-b border-border p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold tracking-tight">Recent Activity</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              {TIME_RANGE_CONFIG[timeRange].label}
            </p>
          </div>

          {/* Settings Cog with Dropdown */}
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground flex-shrink-0"
                >
                  <Settings className="h-3.5 w-3.5" />
                  <span className="sr-only">Time range settings</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => setTimeRange('week')}
                  className={timeRange === 'week' ? 'bg-accent' : ''}
                >
                  Last Week
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setTimeRange('14days')}
                  className={timeRange === '14days' ? 'bg-accent' : ''}
                >
                  Last 14 Days
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setTimeRange('month')}
                  className={timeRange === 'month' ? 'bg-accent' : ''}
                >
                  Last Month
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Close Button */}
            {onToggleCollapse && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleCollapse}
                className="h-7 w-7 text-muted-foreground hover:text-foreground flex-shrink-0"
                title="Close Recent Activity"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content area with scroll - Vertical Timeline */}
      <div className="flex-1 overflow-y-auto p-4">
        {recentActivity.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            No activity found for the selected time period
          </div>
        ) : (
          <div className="relative">
            {/* Vertical timeline line */}
            <div className="absolute left-[11px] top-0 bottom-0 w-[2px] bg-border" />

            {/* Timeline items */}
            <div className="space-y-6">
              {recentActivity.map((transaction) => {
                const transactionStyle = getTransactionStyle(transaction.type);
                const Icon = transactionStyle.icon;

                return (
                  <div
                    key={transaction.id}
                    onClick={() => navigate(`/enquiry?transactionId=${transaction.id}`)}
                    className="relative pl-8 cursor-pointer group"
                  >
                    {/* Timeline icon - smaller with two-tone styling */}
                    <div className={`absolute left-0 top-1 w-6 h-6 rounded-md border-2 ${transactionStyle.bgColor} ${transactionStyle.borderColor} flex items-center justify-center transition-transform group-hover:scale-110`}>
                      <Icon className="h-3.5 w-3.5 text-foreground" />
                    </div>

                    {/* Content card */}
                    <div className="rounded-lg border border-border bg-card p-3 hover:shadow-md hover:border-primary/50 transition-all duration-200">
                      {/* Header with asset name and status */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h4 className="font-medium text-sm group-hover:text-primary transition-colors line-clamp-2 flex-1">
                          {transaction.assetName}
                        </h4>
                        <Badge
                          variant={
                            transaction.status === 'posted'
                              ? 'default'
                              : transaction.status === 'approved'
                                ? 'secondary'
                                : 'outline'
                          }
                          className="text-[10px] px-1.5 py-0 h-5 flex-shrink-0"
                        >
                          {formatStatusText(transaction.status)}
                        </Badge>
                      </div>

                      {/* Transaction type and amount */}
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 h-5 capitalize"
                        >
                          {transaction.type}
                        </Badge>
                        <p className="text-xs font-semibold text-foreground">
                          £{transaction.amount.toLocaleString('en-GB', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                      </div>

                      {/* Date */}
                      <p className="text-[10px] text-muted-foreground">
                        {format(new Date(transaction.transactionDate), 'dd MMM yyyy, HH:mm')}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
